"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShapeBase = void 0;
const gl_matrix_1 = require("gl-matrix");
const types_1 = require("../types");
const glme = require("../math/gl-matrix-extensions");
const Vec2_1 = require("../math/Vec2");
const math_1 = require("../math");
const Adapt_1 = require("../modifiers/Adapt");
const Utilities_1 = require("../Utilities");
const SceneChild_1 = require("../SceneChild");
const tmpMatrix = gl_matrix_1.mat4.create();
const transformMatrix = gl_matrix_1.mat4.create();
const perspectiveMatrix = gl_matrix_1.mat4.create();
const repetitionMatrix = gl_matrix_1.mat4.create();
/**
 * Main class for shape generation
 *
 * @category Scene
 * @abstract
 * @class ShapeBase
 * @order 4
 * @extends {SceneChild}
 */
class ShapeBase extends SceneChild_1.SceneChild {
    /**
     * Creates an instance of ShapeBase
     *
     * @param {ISceneChildSettings} [settings={}]
     */
    constructor(settings = {}) {
        super(settings);
        /**
         * Flag used to determine if indexedBuffer has been generated
         *
         * @internal
         * @ignore
         */
        this.bIndexed = false;
        /**
         * Array used for index a vertex buffer
         * only for first level scene children
         *
         * @internal
         * @ignore
         */
        this.indexedBuffer = [];
        /**
         * The bounding inside the scene
         *
         * @type {IShapeBounding}
         */
        this.bounding = {
            cx: 0,
            cy: 0,
            x: -1,
            y: -1,
            width: 2,
            height: 2,
        };
        this.props = {
            distance: settings.distance,
            repetitions: settings.repetitions,
            rotateX: settings.rotateX,
            rotateY: settings.rotateY,
            rotateZ: settings.rotateZ,
            skewX: settings.skewX,
            skewY: settings.skewY,
            squeezeX: settings.squeezeX,
            squeezeY: settings.squeezeY,
            displace: settings.displace,
            translate: settings.translate,
            scale: settings.scale,
            transformOrigin: settings.transformOrigin,
            perspective: settings.perspective,
            perspectiveOrigin: settings.perspectiveOrigin,
        };
        this.anchor =
            settings.anchor && Array.isArray(settings.anchor)
                ? [
                    typeof settings.anchor[0] === 'number'
                        ? Utilities_1.clamp(-1, 1, settings.anchor[0]) * -1
                        : settings.anchor[0] === 'left'
                            ? 1
                            : settings.anchor[0] === 'right'
                                ? -1
                                : 0,
                    typeof settings.anchor[1] === 'number'
                        ? Utilities_1.clamp(-1, 1, settings.anchor[1]) * -1
                        : settings.anchor[1] === 'top'
                            ? 1
                            : settings.anchor[1] === 'bottom'
                                ? -1
                                : 0,
                ]
                : [0, 0];
        this.boundingType =
            typeof settings.boundingType === 'string'
                ? settings.boundingType === 'relative'
                    ? types_1.EBoundingType.Relative
                    : types_1.EBoundingType.Fixed
                : settings.boundingType || types_1.EBoundingType.Fixed;
        this.vertexCallback = settings.vertexCallback;
    }
    /**
     * Check if the shape should be generated every time
     *
     * @returns {boolean}
     */
    isStatic() {
        const props = this.props;
        return (typeof props.repetitions !== 'function' &&
            typeof props.distance !== 'function' &&
            typeof props.displace !== 'function' &&
            typeof props.scale !== 'function' &&
            typeof props.translate !== 'function' &&
            typeof props.skewX !== 'function' &&
            typeof props.skewY !== 'function' &&
            typeof props.squeezeX !== 'function' &&
            typeof props.squeezeY !== 'function' &&
            typeof props.rotateX !== 'function' &&
            typeof props.rotateY !== 'function' &&
            typeof props.rotateZ !== 'function' &&
            typeof props.transformOrigin !== 'function' &&
            typeof props.perspective !== 'function' &&
            typeof props.perspectiveOrigin !== 'function');
    }
    /**
     * Check if the indexedBuffer array needs to be recreated every time,
     * this can happen when a shape generates an array of vertices different in length at each repetition
     *
     * @returns {boolean}
     */
    isStaticIndexed() {
        return typeof this.props.repetitions !== 'function';
    }
    /**
     * Return a prop value
     *
     * @param {keyof ISceneChildProps} key
     * @param {PropArguments} [propArguments]
     * @param {*} [defaultValue]
     * @returns {*}
     */
    getProp(key, propArguments, defaultValue) {
        let attribute = this.props[key];
        if (typeof attribute === 'function') {
            attribute = attribute(propArguments);
        }
        return typeof attribute === 'undefined' || Number.isNaN(attribute) ? defaultValue : attribute;
    }
    /**
     * Set a single or multiple props
     *
     * @param {(keyof ISceneChildProps<PropArguments> | ISceneChildProps<PropArguments>)} key
     * @param {*} [value]
     * @param {boolean} [bClearIndexed=false]
     */
    setProp(key, value, bClearIndexed = false) {
        if (typeof key === 'string') {
            bClearIndexed = bClearIndexed || key == 'repetitions';
            this.props[key] = value;
        }
        else {
            bClearIndexed = bClearIndexed || 'repetitions' in key;
            Object.keys(key).forEach((k) => (this.props[k] = key[k]));
        }
        this.clearBuffer(bClearIndexed, true);
    }
    /**
     *  Unset buffer
     *
     * @param {boolean} [bClearIndexed=false]
     * @param {boolean} [bPropagateToParents=false]
     * @param {boolean} [bPropagateToChildren=false]
     */
    clearBuffer(bClearIndexed = false, bPropagateToParents = true) {
        this.buffer = undefined;
        if (bClearIndexed) {
            this.bIndexed = false;
            this.indexedBuffer = [];
        }
        this.bStatic = this.isStatic();
        this.bStaticIndexed = this.isStaticIndexed();
        if (bPropagateToParents && this.scene && !this.scene.isFirstLevelChild(this)) {
            const parents = this.scene.getParentsOfSceneChild(this);
            parents.length > 0 && parents[parents.length - 1].clearBuffer(bClearIndexed, bPropagateToParents /* true */);
        }
    }
    /**
     * Update the vertex array if the shape is not static and update the indexedBuffer if it is also not static
     *
     * @param {number} generateId generation id
     * @param {boolean} [bDirectSceneChild=false] adjust shape of center of scene
     * @param {PropArguments} [parentPropArguments]
     */
    generate(generateId = 0, bDirectSceneChild = false, parentPropArguments) {
        var _a, _b;
        if (this.buffer && this.bStatic) {
            return;
        }
        this.generateId = generateId;
        if (!this.bStaticIndexed || !this.bIndexed)
            this.indexedBuffer = [];
        const propArguments = ShapeBase.getEmptyPropArguments(this, parentPropArguments);
        const repetition = propArguments.repetition;
        const repetitions = this.getProp('repetitions', propArguments, 1);
        const repetitionType = Array.isArray(repetitions) ? types_1.ERepetitionType.Matrix : types_1.ERepetitionType.Ring;
        const repetitionCount = Array.isArray(repetitions)
            ? repetitions[0] * ((_a = repetitions[1]) !== null && _a !== void 0 ? _a : repetitions[0])
            : repetitions;
        const repetitionRowCount = Array.isArray(repetitions) ? repetitions[0] : repetitionCount;
        const repetitionColCount = Array.isArray(repetitions) ? (_b = repetitions[1]) !== null && _b !== void 0 ? _b : repetitions[0] : 1;
        const rowRepetition = repetition.row;
        rowRepetition.count = repetitionRowCount;
        const colRepetition = repetition.col;
        colRepetition.count = repetitionColCount;
        repetition.count = repetitionCount;
        repetition.col.count = repetitionColCount;
        repetition.row.count = repetitionRowCount;
        repetition.type = repetitionType;
        let totalBufferLength = 0;
        const buffers = [];
        let currentIndex = 0;
        const centerMatrix = gl_matrix_1.vec2.fromValues((repetitionColCount - 1) / 2, (repetitionRowCount - 1) / 2);
        const sceneAnchor = this.scene ? [this.scene.anchor[0], this.scene.anchor[1], 0] : [0, 0, 0];
        const tmpTotalShapeBounding = [undefined, undefined, undefined, undefined];
        const tmpSingleRepetitionBounding = [undefined, undefined, undefined, undefined];
        for (let currentRowRepetition = 0; currentRowRepetition < repetitionRowCount; currentRowRepetition++) {
            for (let currentColRepetition = 0; currentColRepetition < repetitionColCount; currentColRepetition++, currentIndex++) {
                repetition.index = currentIndex + 1;
                repetition.offset = repetitionCount > 1 ? currentIndex / (repetitionCount - 1) : 1;
                repetition.angle = repetitionType === types_1.ERepetitionType.Ring ? (math_1.PI2 / repetitionCount) * currentIndex : 0;
                colRepetition.index = currentColRepetition + 1;
                colRepetition.offset = repetitionColCount > 1 ? currentColRepetition / (repetitionColCount - 1) : 1;
                rowRepetition.index = currentRowRepetition + 1;
                rowRepetition.offset = repetitionRowCount > 1 ? currentRowRepetition / (repetitionRowCount - 1) : 1;
                // Generate primitives buffer recursively
                const buffer = this.generateBuffer(generateId, propArguments);
                const bufferLength = buffer.length;
                const bounding = this.getShapeBounding();
                buffers[currentIndex] = new Float32Array(bufferLength);
                totalBufferLength += bufferLength;
                {
                    const distance = glme.toVec2(this.getProp('distance', propArguments, glme.VEC2_ZERO));
                    const displace = this.getProp('displace', propArguments, 0);
                    const scale = glme.toVec3(this.getProp('scale', propArguments, glme.VEC2_ONE), 1);
                    const translate = glme.toVec3(this.getProp('translate', propArguments, glme.VEC2_ZERO), 0);
                    const skewX = this.getProp('skewX', propArguments, 0);
                    const skewY = this.getProp('skewY', propArguments, 0);
                    const squeezeX = this.getProp('squeezeX', propArguments, 0);
                    const squeezeY = this.getProp('squeezeY', propArguments, 0);
                    const rotateX = this.getProp('rotateX', propArguments, 0);
                    const rotateY = this.getProp('rotateY', propArguments, 0);
                    const rotateZ = this.getProp('rotateZ', propArguments, 0);
                    const perspective = Utilities_1.clamp(0, 1, this.getProp('perspective', propArguments, 0));
                    const perspectiveOrigin = glme.toVec3(this.getProp('perspectiveOrigin', propArguments, glme.VEC2_ZERO), 0);
                    const transformOrigin = glme.toVec3(this.getProp('transformOrigin', propArguments, glme.VEC2_ZERO), 0);
                    let offset;
                    switch (repetitionType) {
                        case types_1.ERepetitionType.Ring:
                            offset = gl_matrix_1.vec3.fromValues(distance[0], 0, 0);
                            gl_matrix_1.vec3.rotateZ(offset, offset, glme.VEC3_ZERO, repetition.angle + displace);
                            break;
                        case types_1.ERepetitionType.Matrix:
                            offset = gl_matrix_1.vec3.fromValues(distance[1] * (currentColRepetition - centerMatrix[0]), distance[0] * (currentRowRepetition - centerMatrix[1]), 0);
                            break;
                    }
                    const perspectiveSize = perspective > 0 ? Math.max(bounding.width, bounding.height) / 2 : 1;
                    const perspectiveValue = perspective > 0 ? perspectiveSize + (1 - perspective) * (perspectiveSize * 10) : 0;
                    const bTransformOrigin = (this.boundingType === types_1.EBoundingType.Relative ? bounding.cx !== 0 || bounding.cy !== 0 : true) ||
                        perspective !== 0 ||
                        transformOrigin[0] !== 0 ||
                        transformOrigin[1] !== 0;
                    const bPerspectiveOrigin = perspectiveOrigin[0] !== 0 || perspectiveOrigin[1] !== 0;
                    if (bTransformOrigin) {
                        if (this.boundingType === types_1.EBoundingType.Relative) {
                            transformOrigin[0] = transformOrigin[0] * (bounding.width / 2) + bounding.cx;
                            transformOrigin[1] = transformOrigin[1] * (bounding.height / 2) + bounding.cy;
                        }
                        else {
                            transformOrigin[0] *= bounding.width / 2;
                            transformOrigin[1] *= bounding.height / 2;
                        }
                        transformOrigin[2] = perspectiveValue;
                    }
                    /**
                     * Create Matrices
                     */
                    {
                        /**
                         * Create Transformation matrix
                         */
                        gl_matrix_1.mat4.identity(transformMatrix);
                        bTransformOrigin && gl_matrix_1.mat4.translate(transformMatrix, transformMatrix, transformOrigin);
                        if (translate[0] !== 0 || translate[1] !== 0)
                            gl_matrix_1.mat4.translate(transformMatrix, transformMatrix, translate);
                        if (skewX !== 0 || skewY !== 0) {
                            glme.fromSkew(tmpMatrix, [skewX, skewY]);
                            gl_matrix_1.mat4.multiply(transformMatrix, transformMatrix, tmpMatrix);
                        }
                        rotateX !== 0 && gl_matrix_1.mat4.rotateX(transformMatrix, transformMatrix, rotateX);
                        rotateY !== 0 && gl_matrix_1.mat4.rotateY(transformMatrix, transformMatrix, rotateY);
                        rotateZ !== 0 && gl_matrix_1.mat4.rotateZ(transformMatrix, transformMatrix, rotateZ);
                        if (scale[0] !== 1 || scale[1] !== 1)
                            gl_matrix_1.mat4.scale(transformMatrix, transformMatrix, scale);
                        bTransformOrigin &&
                            gl_matrix_1.mat4.translate(transformMatrix, transformMatrix, gl_matrix_1.vec3.scale(transformOrigin, transformOrigin, -1));
                        /**
                         * Create Perspective matrix
                         */
                        if (perspectiveValue > 0) {
                            if (bPerspectiveOrigin) {
                                if (this.boundingType === types_1.EBoundingType.Relative) {
                                    perspectiveOrigin[0] = perspectiveOrigin[0] * (bounding.width / 2) + bounding.cx;
                                    perspectiveOrigin[1] = perspectiveOrigin[1] * (bounding.height / 2) + bounding.cy;
                                }
                                else {
                                    perspectiveOrigin[0] *= bounding.width / 2;
                                    perspectiveOrigin[1] *= bounding.height / 2;
                                }
                                perspectiveOrigin[2] = 0;
                            }
                            gl_matrix_1.mat4.perspective(perspectiveMatrix, -Math.PI / 2, 1, 0, Infinity);
                        }
                        /**
                         * Create Repetition matrix
                         */
                        gl_matrix_1.mat4.identity(repetitionMatrix);
                        gl_matrix_1.mat4.translate(repetitionMatrix, repetitionMatrix, offset);
                        if (bDirectSceneChild) {
                            gl_matrix_1.mat4.translate(repetitionMatrix, repetitionMatrix, sceneAnchor);
                        }
                        /**
                         * Apply anchor
                         */
                        const shapeAnchor = [this.anchor[0] * (bounding.width / 2), this.anchor[1] * (bounding.height / 2), 0];
                        gl_matrix_1.mat4.translate(repetitionMatrix, repetitionMatrix, shapeAnchor);
                        if (repetitionType === types_1.ERepetitionType.Ring)
                            gl_matrix_1.mat4.rotateZ(repetitionMatrix, repetitionMatrix, repetition.angle + displace);
                    }
                    Adapt_1.Bounding.clear(tmpSingleRepetitionBounding);
                    // Apply matrices on vertex
                    for (let bufferIndex = 0; bufferIndex < bufferLength; bufferIndex += 2) {
                        const vertex = [buffer[bufferIndex], buffer[bufferIndex + 1], perspectiveValue];
                        {
                            // Apply squeeze, can be insert into transformMatrix?
                            squeezeX !== 0 && Vec2_1.default.squeezeX(vertex, squeezeX);
                            squeezeY !== 0 && Vec2_1.default.squeezeY(vertex, squeezeY);
                            // Apply transforms
                            gl_matrix_1.vec3.transformMat4(vertex, vertex, transformMatrix);
                            // Apply perspective
                            if (perspectiveValue > 0) {
                                bPerspectiveOrigin && gl_matrix_1.vec3.add(vertex, vertex, perspectiveOrigin);
                                gl_matrix_1.vec3.transformMat4(vertex, vertex, perspectiveMatrix);
                                gl_matrix_1.vec3.scale(vertex, vertex, perspectiveValue);
                                bPerspectiveOrigin && gl_matrix_1.vec3.sub(vertex, vertex, perspectiveOrigin);
                            }
                            // apply repetition matrix
                            gl_matrix_1.vec3.transformMat4(vertex, vertex, repetitionMatrix);
                            // custom vertex manipulation
                            if (typeof this.vertexCallback !== 'undefined') {
                                const index = bufferIndex / 2;
                                const count = bufferLength / 2;
                                const vertexRepetition = {
                                    index: index + 1,
                                    count,
                                    offset: count > 1 ? index / (count - 1) : 1,
                                };
                                this.vertexCallback(vertex, vertexRepetition, propArguments);
                            }
                        }
                        buffers[currentIndex][bufferIndex] = vertex[0];
                        buffers[currentIndex][bufferIndex + 1] = vertex[1];
                        Adapt_1.Bounding.add(tmpSingleRepetitionBounding, vertex[0], vertex[1]);
                        Adapt_1.Bounding.add(tmpTotalShapeBounding, vertex[0], vertex[1]);
                    }
                }
                // Bounding.sum(tmpTotalShapeBounding, tmpSingleRepetitionBounding)
                // After buffer creation, add a frame into indexedBuffer if not static or update bounding
                const singleRepetitionBounding = { cx: 0, cy: 0, x: -1, y: -1, width: 2, height: 2 };
                Adapt_1.Bounding.bind(singleRepetitionBounding, tmpSingleRepetitionBounding);
                if (!this.bStaticIndexed || !this.bIndexed) {
                    this.addIndex(bufferLength, repetition, singleRepetitionBounding);
                }
            }
        }
        Adapt_1.Bounding.bind(this.bounding, tmpTotalShapeBounding);
        this.buffer = new Float32Array(totalBufferLength);
        for (let i = 0, offset = 0, len = buffers.length; i < len; offset += buffers[i].length, i++)
            this.buffer.set(buffers[i], offset);
        this.bIndexed = true;
    }
    /**
     * Return current shape (whit repetions) bounding
     *
     * @return {*}  {IShapeBounding}
     */
    getBounding() {
        return this.bounding;
    }
    /**
     * Get number of repetitions
     *
     * @returns {number}
     */
    getRepetitionCount() {
        var _a;
        const repetitions = this.getProp('repetitions', undefined, 1);
        return Array.isArray(repetitions) ? repetitions[0] * ((_a = repetitions[1]) !== null && _a !== void 0 ? _a : repetitions[0]) : repetitions;
    }
    /**
     * Return buffer
     *
     * @returns {(Float32Array | undefined)}
     */
    getBuffer() {
        return this.buffer;
    }
    /**
     * Return indexed buffer
     *
     * @returns {(Array<IBufferIndex<Props, PropArguments>> | undefined)}
     */
    getIndexedBuffer() {
        return this.indexedBuffer;
    }
    /**
     * Return number of encapsulation
     *
     * @param {IBufferIndex} index
     * @returns {number}
     */
    static getIndexParentLevel(index) {
        if (typeof index.parent === 'undefined')
            return 0;
        let currentParent = index.parent;
        let currentParentLevel = 1;
        while (typeof currentParent.parent !== 'undefined') {
            currentParentLevel++;
            currentParent = currentParent.parent;
        }
        return currentParentLevel;
    }
    /**
     * Stream buffer
     *
     * @param {(TStreamCallback} callback
     */
    stream(callback) {
        if (this.buffer && this.indexedBuffer) {
            for (let i = 0, j = 0, len = this.indexedBuffer.length; i < len; i++) {
                const currentIndexing = this.indexedBuffer[i];
                callback({
                    buffer: this.buffer,
                    frameLength: currentIndexing.frameLength,
                    frameBufferIndex: j,
                    currentIndexing: currentIndexing,
                    currentShapeIndex: i,
                    totalShapes: len,
                });
                j += currentIndexing.frameLength;
            }
        }
    }
    /**
     * Return empty propArguments
     *
     * @static
     * @param {ShapeBase} shape
     * @return {*}  {PropArguments}
     */
    static getEmptyPropArguments(shape, parentPropArguments) {
        const repetition = {
            type: types_1.ERepetitionType.Ring,
            angle: 0,
            index: 1,
            offset: 1,
            count: 1,
            row: { index: 1, offset: 1, count: 1 },
            col: { index: 1, offset: 1, count: 1 },
        };
        return {
            repetition,
            shape,
            parent: parentPropArguments,
        };
    }
}
exports.ShapeBase = ShapeBase;
/**
 * Empty buffer
 *
 * @internal
 * @ignore
 */
ShapeBase.EMPTY_BUFFER = new Float32Array(0);
/**
 * Empty BaseRepetition
 *
 * @internal
 * @ignore
 */
ShapeBase.getEmptySimpleRepetition = () => ({
    index: 1,
    offset: 1,
    count: 1,
});
/**
 * Empty Repetition
 *
 * @internal
 * @ignore
 */
ShapeBase.getEmptyRepetition = () => ({
    type: types_1.ERepetitionType.Ring,
    angle: 0,
    ...ShapeBase.getEmptySimpleRepetition(),
    row: ShapeBase.getEmptySimpleRepetition(),
    col: ShapeBase.getEmptySimpleRepetition(),
});
//# sourceMappingURL=ShapeBase.js.map